// Attributes editor as a right-side drawer, replacing the old clipping popover
// (8 attribute types didn't fit a 280px popover without scroll-inside-scroll).
// Option-building logic (eavTypes + extra unbound keys + canonical options) is
// ported verbatim from attributes-popover-cell.tsx — same behavior, new container.
import {
  Box,
  Button,
  ChakraSelect,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Text,
} from '@elkaso-app/web-design';
import type { AttributeDictionaryType } from 'apis/product-attributes';
import { dictionaryLabelFor, eavTypes } from 'apis/product-attributes';
import { buildMultiCanonicalOptions } from 'pages/exclusive-deals/utils/canonical-options';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rowLabel: string;
  attributes: Record<string, string[]>;
  dictionary: AttributeDictionaryType[] | undefined;
  onChange: (next: Record<string, string[]>) => void;
}

export const AttributesDrawer = ({ isOpen, onClose, rowLabel, attributes, dictionary, onChange }: Props) => {
  const types = eavTypes(dictionary);
  // Picked-but-unbound types stay editable so switching category never hides data.
  const extraKeys = Object.keys(attributes).filter((key) => !types.some((t) => t.key === key));

  const setValue = (key: string, values: string[]) => {
    const next = { ...attributes };
    if (values.length > 0) next[key] = values;
    else delete next[key];
    onChange(next);
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='sm'>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottom='1px solid' borderColor='gray.100'>
          Attributes — {rowLabel}
        </DrawerHeader>
        <DrawerBody display='flex' flexDirection='column' gap='3' py='4'>
          {types.length === 0 && extraKeys.length === 0 && (
            <Text fontSize='sm' color='gray.500'>
              No attribute types configured for this category.
            </Text>
          )}
          {types.map((type) => {
            const current = attributes[type.key] ?? [];
            const options = buildMultiCanonicalOptions(
              type.values.map((v) => v.value),
              current,
              dictionaryLabelFor(dictionary, type.key)
            );
            const selected = options.filter((o) => current.includes(o.value));
            return (
              <FormControl key={type.key}>
                <FormLabel fontSize='xs' mb='1'>
                  {type.label}
                </FormLabel>
                <ChakraSelect
                  size='sm'
                  isMulti
                  options={options}
                  value={selected}
                  onChange={(opts: any) =>
                    setValue(
                      type.key,
                      (opts ?? []).map((o: any) => o.value)
                    )
                  }
                  placeholder={type.label}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 10001 }) }}
                />
              </FormControl>
            );
          })}
          {extraKeys.map((key) => {
            const current = attributes[key] ?? [];
            const options = buildMultiCanonicalOptions([], current);
            return (
              <FormControl key={key}>
                <FormLabel fontSize='xs' mb='1'>
                  {key} (unbound)
                </FormLabel>
                <Box>
                  <ChakraSelect
                    size='sm'
                    isMulti
                    options={options}
                    value={options.filter((o) => current.includes(o.value))}
                    onChange={(opts: any) =>
                      setValue(
                        key,
                        (opts ?? []).map((o: any) => o.value)
                      )
                    }
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 10001 }) }}
                  />
                </Box>
              </FormControl>
            );
          })}
        </DrawerBody>
        <DrawerFooter borderTop='1px solid' borderColor='gray.100'>
          <Button size='sm' colorScheme='red' onClick={onClose}>
            Done
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
